/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';

import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import InstallmentAdvanceTable from 'components/base/installmentAdvanceTable';

import installmentReportsService from 'service/installmentReports';
// import Button from 'components/base/Button';
import { useAppContext } from 'providers/AppProvider';
import { pageToOffset } from 'utils/pageToOffset';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';

interface InstallmentReportTableProps {
  selectedCurrencyPid?: number;
  dateFrom: any;
  dateTo: any;
  setUpdatePaymentBox: any;
}

interface SimplifiedRoleType {
  name: string;
  phone: string;
  children?: SimplifiedRoleType[];
  report_product?: number;
  report_total?: number;
  paid?: number;
  remaining?: string;
  monthly_payment?: number;
  status: string;
  progress?: string;
  actions?: string;
  bg_color: string;
  text_color: string;
  paid_items: number;
  total_items: number;
  gupid?: string;
}

const InstallmentReportTable = ({
  selectedCurrencyPid,
  dateFrom,
  dateTo,
  setUpdatePaymentBox
}: InstallmentReportTableProps) => {
  const { t, i18n } = useTranslation();

  const {
    config: { theme }
  } = useAppContext();

  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  /* prettier-ignore */
  const adjustedDateTo =
   !dateFrom && !dateTo
     ? undefined
     : dateTo === dateFrom
       ? dateFrom + 86400000
       : dateTo
         ? dateTo + 86400000 
         : undefined;

  const { data, isFetching, isLoading, refetch } = useQuery(
    [
      'GET_INSTALLMENT_CLIENT',
      selectedCurrencyPid,
      currentPage,
      adjustedDateTo,
      dateFrom
    ],
    async () => {
      if (selectedCurrencyPid !== 0) {
        const res = await installmentReportsService
          .getListInstallmentClient(selectedCurrencyPid, {
            date_from: dateFrom,
            date_to: adjustedDateTo,
            limit: 10,
            offset: pageToOffset(currentPage, 10)
          })
          .then((res: any) => {
            return res;
          });
        return res;
      }
    },
    {
      enabled:
        !!selectedCurrencyPid || !!currentPage || !!dateFrom || !!adjustedDateTo
    }
  );

  const loading = isLoading || isFetching;

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const installmentReportData: any[] = useMemo(() => {
    return (
      data?.items?.map((items: any) => {
        return {
          name: items?.customer?.first_name || '',
          phone: items?.customer?.phone_number || '',
          report_product: items?.product_name || '',
          report_total: items?.total_amount || 0,
          paid: items?.total_paid_amount || 0,
          remaining: items?.total_waiting_amount || 0,
          monthly_payment: items?.initial_amount || 0,
          status: items?.state || '',
          children:
            items?.installment_items?.map((child: any) => ({
              can_pay: child?.can_pay || false,
              children_pid: child?.pid || 0,
              name: child?.maturity_date || '',
              price: child?.amount || 0,
              status: child?.state || ''
            })) || [],
          paid_items: items?.paid_items || 0,
          total_items: items?.total_items || 0,
          gupid: items?.pid
        };
      }) ?? []
    );
  }, [data, t, i18n?.language]);

  const installmentReportTableColumns: ColumnDef<SimplifiedRoleType>[] =
    useMemo(
      () => [
        {
          accessorKey: 'name',
          header: t('client_name'),
          cell: (rowData: any) => {
            return (
              <div>
                {rowData?.row?.original?.children?.length > 0 ? (
                  <span style={{ fontWeight: 'bold' }}>
                    {rowData?.row?.original?.name}
                  </span>
                ) : (
                  <span>{rowData?.row?.original?.name}</span>
                )}
              </div>
            );
          },
          meta: {
            cellProps: {
              className: 'text-900',
              style: { whiteSpace: 'nowrap' }
            }
          }
        },
        {
          accessorKey: 'phone',
          header: t('phone'),
          meta: {
            cellProps: {
              className: 'text-900',
              style: { whiteSpace: 'nowrap' }
            }
          }
        },
        {
          accessorKey: 'report_product',
          header: t('report_product'),
          meta: {
            cellProps: {
              className: 'text-900',
              style: { whiteSpace: 'nowrap' }
            }
          }
        },
        {
          accessorKey: 'report_total',
          header: t('report_total'),
          meta: {
            cellProps: {
              className: 'text-900',
              style: { whiteSpace: 'nowrap' }
            }
          }
        },
        {
          accessorKey: 'paid',
          header: t('paid'),
          meta: {
            cellProps: {
              className: 'text-900',
              style: { whiteSpace: 'nowrap' }
            }
          }
        },
        {
          accessorKey: 'remaining',
          header: t('remaining'),
          meta: {
            cellProps: {
              className: 'text-900',
              style: { whiteSpace: 'nowrap' }
            }
          }
        },
        {
          accessorKey: 'monthly_payment',
          header: t('monthly_payment'),
          meta: {
            cellProps: {
              className: 'text-900',
              style: { whiteSpace: 'nowrap' }
            }
          }
        },
        {
          accessorKey: 'status',
          header: t('status'),
          cell: ({ row }: any) => {
            const { status, bg_color, text_color } = row.original || {};

            /* prettier-ignore */
            const statusText =
              status !== undefined
                ? status === 'EXPIRED'
                  ? t('overdue')
                  : status === 'PAID'
                    ? t('paid')
                    : status === 'WAITING'
                      ? t('remaining')
                      : status === 'COMING'
                        ? t('coming')
                        : status === 'PARTIALLY_PAID'
                          ? t('partially_paid')
                          : status // fallback to original status
                : '';
            /* prettier-ignore */
            const defaultColors: Record<string, { bg: string; text: string }> =
              {
                EXPIRED: { bg: '#fee2e2', text: '#b91c1c' },
                PAID: { bg: '#dcfce7', text: '#15803d' },
                WAITING: { bg: '#fef9c3', text: '#ca8a04' },
                COMING: { bg: '#e0e7ff', text: '#3730a3' },
                PARTIALLY_PAID: { bg: '#fed7aa', text: '#9a3412' }
              };

            const backgroundColor =
              bg_color || defaultColors[status]?.bg || '#ffffff';
            const textColor =
              text_color || defaultColors[status]?.text || '#000000';

            return (
              <div
                style={{
                  background: backgroundColor,
                  padding: '8px 10px',
                  textAlign: 'center',
                  borderRadius: '8px',
                  color: textColor,
                  fontWeight: '600'
                }}
              >
                {statusText}
              </div>
            );
          },
          meta: {
            cellProps: {
              className: 'text-900',
              style: { whiteSpace: 'nowrap' }
            }
          }
        },
        {
          accessorKey: 'progress',
          header: t('progress'),
          cell: ({ row }: any) => {
            const { paid_items, total_items } = row.original || {};
            const progress =
              total_items > 0 ? (paid_items / total_items) * 100 : 0;

            const backgroundColor = theme === 'dark' ? '#fff' : '#374151';
            const color = theme === 'dark' ? '#fff' : '#000';
            return (
              <div
                className="progress-bar-container"
                style={{
                  width: '100%',
                  height: '6px',
                  backgroundColor: `${backgroundColor}`,
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}
              >
                <div
                  className="progress-bar"
                  style={{
                    width: `${progress}%`,
                    height: '100%',
                    backgroundColor: '#00cc99',
                    borderRadius: '3px 0 0 3px'
                  }}
                />
                <span
                  style={{
                    fontSize: '11px',
                    position: 'absolute',
                    color: `${color}`,
                    marginLeft: '5px',
                    lineHeight: '20px'
                  }}
                >
                  {paid_items}/{total_items} ({progress.toFixed(0)}%)
                </span>
              </div>
            );
          },
          meta: {
            headerProps: { style: { width: '10%' } },
            cellProps: {
              className: 'text-900',
              style: { whiteSpace: 'nowrap' }
            }
          }
        }
        //   {
        //     accessorKey: 'actions',
        //     header: t('actions'),
        //     cell: () => {
        //       return (
        //         <Button variant="outline-primary" loadingPosition="start">
        //           {t('remind_me')}
        //         </Button>
        //       );
        //     },
        //     meta: {
        //       headerProps: { style: { width: '10%' } },
        //       cellProps: { className: 'text-900' }
        //     }
        //   }
      ],
      [t, theme]
    );

  const table = useAdvanceTable({
    data: installmentReportData,
    columns: installmentReportTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
          <InstallmentAdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            data={installmentReportData}
            columns={installmentReportTableColumns}
            refetch={refetch}
            setUpdatePaymentBox={setUpdatePaymentBox}
          />
          {loading && (
            <div className="loading-table">
              <span>
                <Spinner
                  animation="border"
                  role="status"
                  style={{ color: '#8200BF', width: '3rem', height: '3rem' }}
                ></Spinner>
              </span>
            </div>
          )}
          <AdvanceTableCPagination
            count={pageCount}
            page={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </AdvanceTableProvider>
    </>
  );
};
export default InstallmentReportTable;
